/**
 * Memoizes an asynchronous request function, caching the result for a specified TTL (time to live).
 * 
 * @param {Function} callback - The asynchronous function to memoize.
 * @param {number} [ttl=5000] - The time in seconds to cache the result. Defaults to 900 s.
 * @returns {Function} A function that, when called, returns a promise that resolves with the memoized result or rejects with an error.
 * 
 */
export function memoRequest(callback, ttl = 900) {
  const state = { promise: undefined, result: undefined, ttl: undefined };

  return async (invalidation, forceUpdate = false) => await new Promise(async (resolve, reject) => {
    const now = new Date().getTime();

    if (state.result !== undefined && !forceUpdate) {
      resolve(state.result);
      if (state.ttl > now) { return }

      // Return current result and refresh it because ot expiration
      resolve = reject = undefined;
    }

    try {
      if (!state.promise || state.ttl <= now) { state.promise = callback() }
      state.result = await state.promise;
      state.ttl = now + ttl * 1000;

      (resolve ?? invalidation)(state.result);
    }
    catch (error) {
      reject?.(error);
    }
  });
}
